import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, Building2, ChevronDown, Check } from 'lucide-react';
import { type TabConfig } from './BottomNav';
import { brand } from '../lib/branding';
import { useWorkspaces } from './shared/WorkspaceProvider';
import { getActiveWorkspaceId } from '../lib/workspaces';

interface CollapsibleSidebarProps {
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onLogout?: () => void;
}

/**
 * CollapsibleSidebar — light icon sidebar (Reference B).
 *
 * • Icon-only by default, expands to labeled (Section 3.2)
 * • Background is a light tint of the brand accent over the surface color so
 *   the rail always carries the tenant's identity without a dark surface
 *   (owner rule, 2026-10-25: no dark themed page, ever; SYS-04)
 * • Active item: solid brand-primary fill with on-brand text
 * • Logout action pinned at the bottom (always reachable, even collapsed)
 * • Width transition at duration-base/ease-standard; labels fade in AFTER the
 *   width transition mostly completes (staggered, Section 5.8)
 */
const STORAGE_KEY = 'sidebar-collapsed';

/** The navigation rail uses a restrained, brand-aware light surface: a whisper
 *  of the tenant accent over the surface token (#FFFFFF = --color-surface).
 *  Keeping the treatment solid makes active states and notification badges
 *  easier to scan. */
function sidebarBackground(accent: string): string {
  return `color-mix(in srgb, ${accent} 6%, #FFFFFF)`;
}

export default function CollapsibleSidebar({ tabs, activeTab, onTabChange, onLogout }: CollapsibleSidebarProps) {
  const { activeWorkspace } = useWorkspaces();
  // Sidebar logo: prefer the workspace's dedicated sidebar logo, then its logo,
  // then the global brand logo.
  const sidebarLogo = activeWorkspace?.branding?.sidebarLogoUrl || activeWorkspace?.branding?.logoUrl || brand.logoUrl;
  const businessName = activeWorkspace?.branding?.businessName || activeWorkspace?.orgName || brand.businessName;
  // Sidebar rail color follows the workspace brand accent (falls back to global).
  const sidebarAccent = activeWorkspace?.branding?.accent || brand.colors.accent || '#059669';

  const [collapsed, setCollapsed] = useState(() => {
    try {
      // Icon-only by default — the brief's "icon-only by default, collapsible to labeled"
      return localStorage.getItem(STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // ── Collapsible nav groups (admin sidebar redesign, Oct 2026) ──────
  // Tabs carrying a `group` render under an expand/collapse header.
  // Open groups are remembered across sessions; the group containing the
  // active page auto-expands. Roles whose tabs set no group are entirely
  // unaffected.
  const GROUPS_STORAGE_KEY = 'sidebar-nav-groups';
  const [openGroups, setOpenGroups] = useState<string[]>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(GROUPS_STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((g) => typeof g === 'string') : [];
    } catch {
      return [];
    }
  });

  const persistOpenGroups = (next: string[]) => {
    try {
      localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
    } catch {}
  };

  const toggleGroup = (name: string) => {
    setOpenGroups((prev) => {
      const next = prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name];
      persistOpenGroups(next);
      return next;
    });
  };

  const activeGroup = tabs.find((t) => t.id === activeTab)?.group;
  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((prev) => {
      if (prev.includes(activeGroup)) return prev;
      const next = [...prev, activeGroup];
      persistOpenGroups(next);
      return next;
    });
  }, [activeGroup]);

  const width = collapsed ? 72 : 248;

  return (
    <aside
      className="sticky top-0 z-[var(--z-nav)] hidden md:flex flex-col h-full overflow-hidden border-r border-border-subtle transition-[width] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
      style={{ width, minWidth: width, background: sidebarBackground(sidebarAccent) }}
      aria-label="Primary navigation"
    >
      {/* Brand header */}
      <div
        className={`flex items-center h-16 px-3 border-b border-border-subtle shrink-0 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!collapsed && (
          <div className="flex items-center gap-3 transition-all duration-300">
            {sidebarLogo ? (
              <img
                src={sidebarLogo}
                alt={businessName}
                className="h-9 w-9 object-contain rounded-lg shrink-0"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-black text-base shrink-0"
                style={{ backgroundColor: brand.colors.primary, boxShadow: `0 4px 8px -2px ${brand.colors.primary}44` }}
              >
                {businessName.charAt(0)}
              </div>
            )}
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-extrabold text-sm tracking-tight text-text-primary truncate">
                {businessName}
              </span>
              <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest truncate">
                Portal
              </span>
            </div>
          </div>
        )}
        <button
          onClick={toggle}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-pressed transition-all cursor-pointer focus:outline-none focus-visible:ring-2 shrink-0"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <PanelLeftOpen size={18} strokeWidth={2} />
          ) : (
            <PanelLeftClose size={18} strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-1">
        {tabs.map((tab, idx) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          const prevTab = idx > 0 ? tabs[idx - 1] : null;
          const showCategoryHeader = tab.category && (!prevTab || prevTab.category !== tab.category);

          const tabButton = (indented: boolean) => (
            <button
              onClick={() => onTabChange(tab.id)}
              className={`group relative w-full flex items-center rounded-xl transition-colors duration-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${
                isActive
                  ? 'bg-brand-primary text-text-on-brand font-bold shadow-sm'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-pressed'
              } ${collapsed ? 'justify-center py-3' : indented ? 'justify-start gap-3 pl-6 pr-3 py-2.5' : 'justify-start gap-3 px-3 py-2.5'}`}
              aria-current={isActive ? 'page' : undefined}
              title={collapsed ? ((tab.group || tab.category) ? `${tab.group || tab.category}: ${tab.label}` : tab.label) : undefined}
            >
              <div className="relative shrink-0 nav-icon">
                <Icon
                  size={20}
                  strokeWidth={isActive ? 2.5 : 1.5}
                  className="transition-all duration-200"
                />
                {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none shadow-sm">
                    {tab.badgeCount > 99 ? '99+' : tab.badgeCount}
                  </span>
                )}
              </div>

              <span
                className={`text-sm whitespace-nowrap overflow-hidden transition-opacity ${
                  collapsed
                    ? 'opacity-0 max-w-0'
                    : 'opacity-100 max-w-[180px]'
                }`}
                style={{
                  transitionDelay: collapsed ? '0ms' : '150ms', // fade labels in after width settles (Section 5.8)
                }}
              >
                {tab.label}
              </span>
            </button>
          );

          // Grouped tab: first child of a run renders the group header;
          // children render only while the group is open — except in the
          // icon rail, where every child stays one click away (a divider
          // marks the group boundary instead of a header).
          if (tab.group) {
            const isGroupStart = !prevTab || prevTab.group !== tab.group;
            const groupOpen = openGroups.includes(tab.group);
            const groupHasActive = tabs.some((t) => t.group === tab.group && t.id === activeTab);
            return (
              <React.Fragment key={tab.id}>
                {isGroupStart &&
                  (collapsed ? (
                    <div className="border-t border-border-subtle my-1.5" aria-hidden="true" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleGroup(tab.group!)}
                      aria-expanded={groupOpen}
                      className="w-full flex items-center gap-1.5 px-3 pt-3 pb-1.5 rounded-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                    >
                      <span
                        className={`text-[10px] font-black tracking-wider uppercase select-none ${
                          groupHasActive ? 'text-brand-primary' : 'text-text-tertiary'
                        }`}
                      >
                        {tab.group}
                      </span>
                      {groupHasActive && <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" aria-hidden="true" />}
                      <ChevronDown
                        size={12}
                        className={`ml-auto text-text-tertiary transition-transform ${groupOpen ? '' : '-rotate-90'}`}
                      />
                    </button>
                  ))}
                {(collapsed || groupOpen) && tabButton(true)}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={tab.id}>
              {showCategoryHeader && (
                <div className={`pt-3 pb-1.5 transition-all ${collapsed ? 'border-t border-border-subtle my-1.5' : 'px-3'}`}>
                  {!collapsed && (
                    <span className="text-[10px] font-black tracking-wider uppercase text-text-tertiary select-none">
                      {tab.category}
                    </span>
                  )}
                </div>
              )}
              {tabButton(false)}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Workspace switcher — pinned above logout, always reachable */}
      <SidebarWorkspaceSwitcher collapsed={collapsed} />

      {/* Logout — pinned to the bottom, always reachable even when collapsed */}
      {onLogout && (
        <div className="shrink-0 px-2.5 pb-3 pt-1">
          <button
            onClick={onLogout}
            className={`group relative w-full flex items-center rounded-xl transition-colors duration-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary text-text-secondary hover:text-text-primary hover:bg-surface-pressed ${
              collapsed ? 'justify-center py-3' : 'justify-start gap-3 px-3 py-3'
            }`}
            title={collapsed ? 'Logout' : undefined}
            aria-label="Logout"
          >
            <LogOut size={20} strokeWidth={1.5} className="shrink-0 transition-all duration-200 group-hover:stroke-[2.5]" />
            <span
              className={`text-sm whitespace-nowrap overflow-hidden transition-opacity ${
                collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[180px]'
              }`}
              style={{ transitionDelay: collapsed ? '0ms' : '150ms' }}
            >
              Logout
            </span>
          </button>
        </div>
      )}

      {/* Bottom fade to indicate scroll */}
      <div className="h-3 shrink-0 pointer-events-none bg-gradient-to-t from-black/5 to-transparent" />
    </aside>
  );
}

/**
 * Sidebar workspace switcher — shows the active workspace and lets users with
 * multiple memberships switch instantly. Hidden when the user belongs to one
 * workspace (or none). Portals the dropdown to document.body.
 */
function SidebarWorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const { memberships, activeWorkspace, switchWorkspace } = useWorkspaces();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Hooks must be called unconditionally — the early return below would
  // otherwise change the hook count between renders (React error #310).
  useEffect(() => {
    if (!open) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ bottom: window.innerHeight - rect.top + 8, left: Math.max(8, rect.right - 280) });
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (memberships.length <= 1) return null;

  const displayName = activeWorkspace?.orgName || activeWorkspace?.name || 'Workspace';

  return (
    <div className="shrink-0 px-2.5 pb-1 pt-1.5 border-t border-border-subtle">
      {/* The switcher is a LIGHT card (owner directive 2026-10-25: no
          dark themed page, ever) on the light sidebar rail (SYS-04);
          its border and shadow keep it distinct from the rail tint. */}
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className={`group relative w-full flex items-center rounded-xl transition-colors duration-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50 bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-sm ${
          collapsed ? 'justify-center py-3' : 'justify-start gap-3 px-3 py-3'
        }`}
        title={collapsed ? displayName : undefined}
        aria-label="Switch workspace"
      >
        <Building2 size={18} strokeWidth={1.5} className="shrink-0" />
        <span
          className={`text-sm whitespace-nowrap overflow-hidden transition-opacity ${
            collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[150px]'
          }`}
          style={{ transitionDelay: collapsed ? '0ms' : '150ms' }}
        >
          {displayName}
        </span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${collapsed ? 'hidden' : 'ml-auto'}`} />
      </button>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={panelRef}
          role="menu"
          className="dropdown-portal w-72 max-w-[85vw] bg-white rounded-2xl shadow-2xl border border-slate-200 py-2"
          style={{ top: pos.bottom, left: pos.left, zIndex: 'var(--z-dropdown)' }}
        >
          <div className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Switch Workspace</div>
          <div className="py-1 max-h-80 overflow-y-auto">
            {memberships.map(m => {
              const active = m.workspaceId === activeWorkspace?.workspaceId;
              return (
                <button
                  key={m.workspaceId}
                  onClick={() => { setOpen(false); switchWorkspace(m.workspaceId); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all cursor-pointer ${active ? 'bg-brand-active' : 'hover:bg-slate-50'}`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'}`}>
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black text-slate-800 truncate">{m.orgName || m.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{m.slug}{m.role ? ` · ${m.role}` : ''}</p>
                  </div>
                  {active && <Check className="h-4 w-4 text-brand shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
